import React, { useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppearance } from '../../contexts/AppearanceContext';
import { intensityToOpacity, resolveBackgroundTheme } from './backgroundThemes';
import type { BackgroundThemeId } from '../../types';

interface AmbientBackgroundProps {
  className?: string;
  tone?: 'light' | 'dark';
  /**
   * `layout` is the shared instance in Layout; it steps aside while a page mounts its own
   * (`page`) instance so only one canvas animates at a time.
   */
  scope?: 'layout' | 'page';
  /** Overrides the user's theme (e.g. admin-configured public home background). */
  theme?: BackgroundThemeId;
  intensity?: number;
}

export const AmbientBackground: React.FC<AmbientBackgroundProps> = ({
  className,
  tone,
  scope = 'page',
  theme: themeOverride,
  intensity: intensityOverride,
}) => {
  const { mode } = useTheme();
  const {
    backgroundActivity,
    backgroundTheme,
    backgroundThemeIntensity,
    hasPageBackground,
    registerPageBackground,
  } = useAppearance();
  const definition = resolveBackgroundTheme(themeOverride ?? backgroundTheme);
  const Theme = definition.component;

  useEffect(() => {
    if (scope !== 'page') return;
    return registerPageBackground();
  }, [registerPageBackground, scope]);

  if (!Theme || (scope === 'layout' && hasPageBackground)) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none overflow-hidden ${className ?? 'absolute inset-0'}`}
      style={{ opacity: intensityToOpacity(intensityOverride ?? backgroundThemeIntensity) }}
    >
      <Theme tone={tone ?? mode} activity={backgroundActivity} />
    </div>
  );
};
